import { Box, Paper, Table, TableBody, TableContainer } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomViewTask from 'components/shared/CustomViewTask/CustomViewTask';
import { ConfirmationModal } from 'components/shared/CustomViewTask/components/ConfirmationModal';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { Operation } from 'model/alerts.model';
import { Task } from 'model/tasks.model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import { AddTaskForm } from '../TasksFilters/components/AddTaskForm';
import { TaskTypeSelectionModal } from '../TasksFilters/components/TaskTypeSelectionModal/TaskTypeSelectionModal';
import { TasksTableHeader } from './components/TasksTableHeader/TasksTableHeader';
import { TasksTableSection } from './components/TasksTableSection/TasksTableSection';
import { STATUS_CONFIG, TaskType } from './constants';
import { useTasksTable } from './hooks/useTasksTable';
import { TasksTableProps } from './types';
import { extractAssignees } from './utils';

export const TasksTable = ({
  mine = false,
  filters,
  refreshTrigger,
  onStatisticsInvalidate,
  tabValue,
}: TasksTableProps) => {
  const location = useLocation();
  const globalSearchItemId = location.state?.globalSearchItemId;
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const [action, setAction] = useState('');
  const [edit, setEdit] = useState(false);
  const [added, setAdded] = useState(false);

  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    TODO: true,
    IN_PROGRESS: true,
    DONE: true,
  });

  const [drawerState, setDrawerState] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [taskTypeModalOpen, setTaskTypeModalOpen] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [initialTaskStatus, setInitialTaskStatus] = useState<string>('');
  const [addTaskDrawerState, setAddTaskDrawerState] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const statusRef = useRef<string>('');

  const handleFetchError = useCallback(
    (message: string) => {
      completeOperation('fetch', message, true);
    },
    [completeOperation],
  );

  const handleFetchStart = useCallback(
    (operation: Operation, message: string) => {
      startOperation(operation, message);
    },
    [startOperation],
  );

  const {
    toDoData,
    inProgressData,
    doneData,
    toDoLoading,
    inProgressLoading,
    doneLoading,
    fetchAllTasks,
    allTasks,
  } = useTasksTable({
    mine,
    filters,
    onError: handleFetchError,
    onStartOperation: handleFetchStart,
  });

  const refetchTasksAndStatistics = useCallback(() => {
    fetchAllTasks();
    onStatisticsInvalidate?.();
  }, [fetchAllTasks, onStatisticsInvalidate]);

  const toggleSection = useCallback((sectionKey: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const handleTaskClick = useCallback((task: Task) => {
    const assignedToData = extractAssignees(task);

    const taskData = {
      ...task,
      name: task.subject,
      nameType: task.nameType,
      priority: Array.isArray(task.priority) ? task.priority[0] : task.priority || 'Normal',
      assignedTo: assignedToData,
      dueDate: task.dueDate || task.date,
      createdDate: task.createdAt || task.createdDate,
      createdAt: task.createdAt || task.createdDate,
      description: task.description,
    };

    setSelectedTask(taskData);
    setDrawerState(true);
  }, []);

  const handleEditTask = useCallback(
    (task: Task) => {
      handleTaskClick(task);
      setTimeout(() => {
        sessionStorage.setItem('triggerTaskEdit', 'true');
      }, 100);
    },
    [handleTaskClick],
  );

  const handleDeleteTask = useCallback((task: Task) => {
    setTaskToDelete(task);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!taskToDelete?.id) return;

    setDeleteLoading(true);
    startOperation('delete', 'Deleting task...');

    try {
      await TaskService.deleteTask(taskToDelete.id);
      completeOperation('delete', 'Task deleted successfully');
      setDeleteModalOpen(false);
      setTaskToDelete(null);
      fetchAllTasks();
      onStatisticsInvalidate?.();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to delete task';
      completeOperation('delete', errorMessage, true);
    } finally {
      setDeleteLoading(false);
    }
  }, [taskToDelete, fetchAllTasks, startOperation, completeOperation, onStatisticsInvalidate]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setTaskToDelete(null);
  }, []);

  const handleAddTaskFromSection = useCallback((status: string) => {
    statusRef.current = status;
    setInitialTaskStatus(status);
    setTaskTypeModalOpen(true);
  }, []);

  const handleTaskTypeSelect = useCallback(
    (taskType: string) => {
      setSelectedTaskType(taskType);
      setTaskTypeModalOpen(false);
      if (!initialTaskStatus && statusRef.current) {
        setInitialTaskStatus(statusRef.current);
      }
      setAddTaskDrawerState(true);
    },
    [initialTaskStatus],
  );

  const handleTaskTypeModalClose = useCallback(() => {
    setTaskTypeModalOpen(false);
    setInitialTaskStatus('');
  }, []);

  useEffect(() => {
    if (added) {
      fetchAllTasks();
      onStatisticsInvalidate?.();
      completeOperation('create', 'Task added successfully');
      setAdded(false);
    }
  }, [added, completeOperation, fetchAllTasks, onStatisticsInvalidate]);

  useEffect(() => {
    if (edit) {
      fetchAllTasks();
      onStatisticsInvalidate?.();
      completeOperation('update', 'Task updated successfully');
      setEdit(false);
    }
  }, [edit, completeOperation, fetchAllTasks, onStatisticsInvalidate]);

  useEffect(() => {
    if (action) {
      fetchAllTasks();
      onStatisticsInvalidate?.();
      const isError =
        !action.includes('successfully') &&
        !action.includes('removed') &&
        !action.includes('moved');
      completeOperation('update', `Task ${action}`, isError);

      setAction('');
    }
    sessionStorage.removeItem('dealSelectedFromOverview');
  }, [action, fetchAllTasks, completeOperation, onStatisticsInvalidate]);

  useEffect(() => {
    if (!addTaskDrawerState) {
      setInitialTaskStatus('');
      setSelectedTaskType(null);
    }
  }, [addTaskDrawerState]);

  useEffect(() => {
    if (selectedTask?.id && drawerState) {
      const updatedTask = allTasks.find((task: any) => task.id === selectedTask.id);
      if (updatedTask) {
        setSelectedTask(updatedTask);
      }
    }
  }, [allTasks, drawerState, selectedTask?.id]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchAllTasks();
    }
  }, [refreshTrigger, fetchAllTasks]);

  useEffect(() => {
    if (globalSearchItemId) {
      const fetchTaskById = async () => {
        try {
          const response = await TaskService.getTaskById(globalSearchItemId);
          if (response?.data) {
            const task = response.data;
            const assignedToData = extractAssignees(task);
            const taskData = {
              ...task,
              name: task.subject || task.name,
              nameType: task.nameType,
              priority: Array.isArray(task.priority) ? task.priority[0] : task.priority || 'Normal',
              assignedTo: assignedToData,
              dueDate: task.dueDate || task.date,
              createdDate: task.createdAt || task.createdDate,
              createdAt: task.createdAt || task.createdDate,
              description: task.description,
            };
            setSelectedTask(taskData);
            setDrawerState(true);
          }
        } catch (error) {
          console.error('Failed to fetch task from search:', error);
        }
      };
      fetchTaskById();
    }
  }, [globalSearchItemId]);

  return (
    <Box>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 450px)' },
          }}
        >
          <Table stickyHeader aria-label='tasks table'>
            <TasksTableHeader />
            <TableBody>
              {STATUS_CONFIG.map((section) => {
                const dataMap: Record<TaskType, typeof toDoData> = {
                  TODO: toDoData,
                  IN_PROGRESS: inProgressData,
                  DONE: doneData,
                };
                const loadingMap: Record<TaskType, boolean> = {
                  TODO: toDoLoading,
                  IN_PROGRESS: inProgressLoading,
                  DONE: doneLoading,
                };
                return (
                  <TasksTableSection
                    key={section.key}
                    section={section}
                    data={dataMap[section.key]}
                    loading={loadingMap[section.key]}
                    isOpen={openSections[section.key]}
                    onToggle={toggleSection}
                    onTaskClick={handleTaskClick}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onStatusChange={refetchTasksAndStatistics}
                    onAddTask={handleAddTaskFromSection}
                    tabValue={tabValue}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.isError}
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <TaskTypeSelectionModal
        isModalOpen={taskTypeModalOpen}
        onClose={handleTaskTypeModalClose}
        onSelect={handleTaskTypeSelect}
      />

      {addTaskDrawerState && selectedTaskType && (initialTaskStatus || statusRef.current) && (
        <AddTaskForm
          key={`add-task-${initialTaskStatus || statusRef.current}-${selectedTaskType}`}
          drawerState={addTaskDrawerState}
          onClose={() => {
            setAddTaskDrawerState(false);
            setSelectedTaskType(null);
            setInitialTaskStatus('');
            statusRef.current = '';
          }}
          taskType={selectedTaskType}
          initialStatus={initialTaskStatus || statusRef.current}
          onTaskAdded={() => {
            setAdded(true);
          }}
        />
      )}

      {drawerState && selectedTask && (
        <CustomViewTask
          setDrawerState={setDrawerState}
          drawerState={drawerState}
          selectedData={selectedTask}
          setEditDone={setEdit}
          setDeleted={(id) => {
            if (id) {
              setAction('deleted successfully');
            }
          }}
          onUpdate={refetchTasksAndStatistics}
          setSelected={setSelectedTask}
          isFromTasksTab={true}
          tabValue={tabValue}
        />
      )}
    </Box>
  );
};
